import React, { useEffect, useState } from 'react';
import Icon from './ui/Icon';
import packageData from '../../package.json';
import './UpdateBanner.css';

// How often an open tab asks whether a newer admin has been published, and the
// shortest gap between checks when focus or visibility changes fire them.
export const CHECK_INTERVAL_MS = 5 * 60 * 1000;
export const MIN_RECHECK_MS = 60 * 1000;

// The build writes /meta.json ({"version": ...}, generate-build-version.js) and
// the deploy uploads it no-cache and invalidates CloudFront, so it names the
// live build. CloudFront answers a missing file with index.html, so anything
// that isn't that JSON is unknown.
export const fetchDeployedVersion = async () => {
  try {
    const response = await window.fetch(`${process.env.PUBLIC_URL || ''}/meta.json`, { cache: 'no-store' });
    if (!response.ok) return null;
    const body = await response.json();
    return body && typeof body.version === 'string' && body.version ? body.version : null;
  } catch (_) {
    return null;
  }
};

// Offers a reload when the published admin differs from the running one. It
// never reloads on its own: that would drop half-filled forms and setup edits.
const UpdateBanner = ({ runningVersion = packageData.version, fetchVersion = fetchDeployedVersion }) => {
  const [available, setAvailable] = useState(null);
  const [dismissed, setDismissed] = useState(null);

  useEffect(() => {
    let active = true;
    let lastCheck = -Infinity;
    const check = async () => {
      if (document.visibilityState === 'hidden' || Date.now() - lastCheck < MIN_RECHECK_MS) return;
      lastCheck = Date.now();
      const version = await fetchVersion();
      if (active && version) setAvailable(version === runningVersion ? null : version);
    };
    check();
    const timer = setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    return () => {
      active = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
    };
  }, [runningVersion, fetchVersion]);

  if (!available || available === dismissed) return null;
  return (
    <div className="update-banner" role="status">
      <span>GateWatcher {available} is available.</span>
      <button type="button" className="update-banner__reload" onClick={() => window.location.reload()}>
        <Icon name="refresh" size={13} /> Reload
      </button>
      <button type="button" className="update-banner__dismiss" aria-label="Dismiss until the next release"
        title="Dismiss until the next release" onClick={() => setDismissed(available)}>
        <Icon name="close" size={13} />
      </button>
    </div>
  );
};

export default UpdateBanner;
